/**
 * Constants
 * Consolidated constants for private loads page including:
 * - API limits and pagination
 * - Filter defaults
 * - UI colors, typography, and layout
 */

import { SEARCH_FILTER_LIMITS } from 'utils/search-filter-validation'

// ============================================================================
// API & PAGINATION LIMITS
// ============================================================================

/** API and pagination limits */
export const LIMITS = {
    LOADS_PER_PAGE: 30,
    CACHE_DURATION_MS: 5 * 60 * 1000, // 5 minutes
    API_TIMEOUT_MS: 30000,
    MAX_RETRIES: 3,
    RETRY_DELAY_MS: 1000,
}

/** Deadhead distance limits — derived from the shared search-filter-validation module. */
export const DHD_LIMITS = {
    DEFAULT: SEARCH_FILTER_LIMITS.DHD_DEFAULT,
    MAX: SEARCH_FILTER_LIMITS.DHD_MAX,
} as const

// ============================================================================
// FILTER DEFAULTS
// ============================================================================

/** Default filter values for new tabs */
export const DEFAULT_FILTERS = {
    origin: null,
    originDhd: DHD_LIMITS.DEFAULT.toString(),
    destination: null,
    destinationDhd: DHD_LIMITS.DEFAULT.toString(),
    lengthFt: '',
    weightLbs: '',
    equipmentTypes: [],
    fullPartial: '',
    startDate: null,
    endDate: null,
    showAllLoads: '',
    loadSources: ['BROKER_PORTAL'],
    companies: [],
    maxMi: '',
    minMi: '',
    minWeight: '',
    minT: '',
    minR: '',
    searchBack: '',
}

/** Default tab configuration for first tab */
export const DEFAULT_TAB = {
    id: '1',
    origin: 'Anywhere',
    destination: 'Anywhere',
    count: 0,
}

/** Available load source types */
export const LOAD_SOURCES = [
    'TELEGRAM',
    'PARADE',
    'HAPPYROBOT',
    'BEON',
    'NAVISPHERE',
    'ARRIVE',
    'TRUCKSTOP',
    'SPOT',
    'EMERGE',
    'ECHO',
    'MODE_GLOBAL',
    'JB_HUNT',
    'KNX',
    'RXO',
    'CONVOY',
    'SCHNEIDER',
    'TRUCKSTOP_ACCOUNT',
    'SYLECTUS',
    'UBER_FREIGHT',
    // 'DAT', // Temporarily hidden
    'DAT_EXTENSION',
    'BROKER_PORTAL',
] as const

/** Available load source options for filtering */
export const LOAD_SOURCE_OPTIONS = [
    { value: 'TELEGRAM', label: 'Telegram', requiresConnection: false },
    { value: 'TQL', label: 'TQL', requiresConnection: false },
    // { value: 'ITF', label: 'ITF', requiresConnection: true },
    // { value: 'PLS', label: 'PLS', requiresConnection: true },
    { value: 'BEON', label: 'Nolan', requiresConnection: false },
    { value: 'NAVISPHERE', label: 'C.H. Robinson', requiresConnection: false },
    { value: 'ARRIVE', label: 'Arrive Logistics', requiresConnection: false },
    { value: 'EMERGE', label: 'Emerge', requiresConnection: false },
    { value: 'ECHO', label: 'Echo', requiresConnection: false },
    { value: 'SPOT', label: 'Spot', requiresConnection: false },
    { value: 'RXO', label: 'RXO', requiresConnection: false },
    { value: 'CONVOY', label: 'Convoy', requiresConnection: false },
    { value: 'UBER_FREIGHT', label: 'Uber Freight', requiresConnection: false },
    { value: 'MODE_GLOBAL', label: 'Mode Global', requiresConnection: false },
    { value: 'JB_HUNT', label: 'J.B. Hunt', requiresConnection: false },
    { value: 'KNX', label: 'KNX', requiresConnection: false },
    { value: 'SCHNEIDER', label: 'Schneider', requiresConnection: false },
    { value: 'TRUCKSTOP_ACCOUNT', label: 'TruckStop Account', requiresConnection: false },
    { value: 'SYLECTUS', label: 'Sylectus', requiresConnection: true },
    // { value: 'DAT', label: 'DAT One', requiresConnection: false }, // Temporarily hidden
    { value: 'DAT_EXTENSION', label: 'DAT', requiresConnection: false },
    { value: 'TRUCKSTOP', label: 'TruckStop Trial', requiresConnection: false },
    { value: 'PARADE', label: 'Parade', requiresConnection: false },
    { value: 'HAPPYROBOT', label: 'HappyRobot', requiresConnection: false },
    { value: 'BROKER_PORTAL', label: 'Numeo Loads', requiresConnection: false },
] as const

/** Sources that always appear available (no connection check needed) */
export const ALWAYS_AVAILABLE_SOURCES = ['TRUCKSTOP', 'PARADE', 'HAPPYROBOT', 'TELEGRAM', 'BROKER_PORTAL'] as const

/** Sources that require the Chrome extension (AIHub) */
export const AIHUB_SOURCES = [
    'TRUCKSTOP_ACCOUNT',
    'BEON',
    'NAVISPHERE',
    'ARRIVE',
    'TQL',
    'EMERGE',
    'ECHO',
    'SPOT',
    'MODE_GLOBAL',
    'KNX',
    'RXO',
    'CONVOY',
    'JB_HUNT',
    'SCHNEIDER',
    'UBER_FREIGHT',
    // 'DAT', // Temporarily hidden
] as const

/** Sources that require loadboard connection (credential-based auth via modal) */
export const LOADBOARD_SOURCES = ['SYLECTUS'] as const

/**
 * Sources handled exclusively by the Chrome extension via direct browser search
 * (not routed through loads-ingest-service).
 * DAT_EXTENSION opens one.dat.com in a background tab, captures a fresh Bearer
 * token, runs the DAT FindLoads GraphQL query, and streams results back.
 */
export const DAT_EXTENSION_SOURCES = ['DAT_EXTENSION'] as const

/** Maps source key (e.g. 'CONVOY') to its AIHub source identifier (e.g. 'convoy').
 *  Single source of truth — used by both load-hub and agentic-hub. */
export const AIHUB_KEY_TO_SOURCE: Record<string, string> = {
    TRUCKSTOP_ACCOUNT: 'truckstop-account',
    BEON: 'beon',
    NAVISPHERE: 'navisphere',
    ARRIVE: 'arrive',
    TQL: 'tql',
    EMERGE: 'emerge',
    ECHO: 'echo',
    SPOT: 'spot',
    RXO: 'rxo',
    CONVOY: 'convoy',
    UBER_FREIGHT: 'uber-freight',
    MODE_GLOBAL: 'mode-global',
    JB_HUNT: 'jb-hunt',
    KNX: 'knx',
    SCHNEIDER: 'schneider',
    DAT: 'dat',
}

/**
 * The inverse: board id → canonical LoadSource ('mode-global' → 'MODE_GLOBAL').
 *
 * Lives here, beside the map it inverts, because three places needed it independently
 * (load-hub board list, agentic-hub source list, connect analytics) and three hand-rolled
 * inversions drift the moment a board is added with a naming quirk.
 */
export const AIHUB_SOURCE_TO_KEY: Record<string, string> = Object.fromEntries(Object.entries(AIHUB_KEY_TO_SOURCE).map(([source, boardId]) => [boardId, source]))

/** Default selected sources for new tabs — "Numeo Loads" (BROKER_PORTAL) is the single
 * consolidated default: it is always available (no connection required) and, server-side,
 * folds in Parade loads. TruckStop Trial / Parade / HappyRobot are no longer surfaced
 * individually in Load Hub (see LOAD_HUB_HIDDEN_SOURCES). */
export const DEFAULT_SELECTED_SOURCES: readonly string[] = ['BROKER_PORTAL']

/**
 * Sources removed from the Load Hub source picker and consolidated into "Numeo Loads"
 * (BROKER_PORTAL). TruckStop Trial no longer returns loads; HappyRobot already posts its
 * loads into Broker Portal; Parade is folded into BROKER_PORTAL server-side
 * (BrokerPortalProvider). Load Hub ONLY. Used to filter the SourceBar options and to
 * normalize any persisted tab selections that still reference them.
 */
export const LOAD_HUB_HIDDEN_SOURCES: readonly string[] = ['TRUCKSTOP', 'PARADE', 'HAPPYROBOT']

/**
 * Strip the now-hidden legacy sources from a persisted tab's `loadSources` so returning
 * users stop searching/seeing them. Falls back to the consolidated default ("Numeo Loads")
 * when stripping would leave a tab with no sources selected.
 */
export const normalizeLoadHubSources = (sources: string[]): string[] => {
    const hidden = new Set(LOAD_HUB_HIDDEN_SOURCES)
    const kept = sources.filter((s) => !hidden.has(s))
    return kept.length > 0 ? kept : [...DEFAULT_SELECTED_SOURCES]
}

/** Available equipment types */
export const EQUIPMENT_TYPES = ['V', 'F', 'D', 'R', 'B', 'T'] as const

/** Full/Partial load options */
export const FULL_PARTIAL_OPTIONS = ['Full', 'Partial'] as const

/** Default MPG value for cost calculations */
export const DEFAULT_MPG = 7

// ============================================================================
// UI COLORS
// ============================================================================

/** Helper function to get theme-aware colors using Joy UI palette */
export const getColors = (isDark: boolean) => ({
    // Brand colors
    BRAND_BLUE: '#0067D5',
    BRAND_NUMEO: '#0029D7',
    BRAND_LIGHT: isDark ? 'var(--joy-palette-primary-900, #1A3B73)' : 'var(--joy-palette-primary-100, #EBEFFF)',

    // Text colors - using Joy UI semantic colors
    TEXT_PRIMARY: 'var(--joy-palette-text-primary, #1F2937)',
    TEXT_SECONDARY: 'var(--joy-palette-text-secondary, #6B7280)',
    TEXT_TERTIARY: 'var(--joy-palette-text-tertiary, #9CA3AF)',
    TEXT_MUTED: 'var(--joy-palette-neutral-500, #D1D5DB)',

    // Background colors - using Joy UI semantic backgrounds
    BG_WHITE: 'var(--joy-palette-background-surface, #FFFFFF)',
    BG_LIGHT: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)',
    BG_SECONDARY: isDark ? 'var(--joy-palette-neutral-800, #1F2937)' : 'var(--joy-palette-neutral-100, #F3F4F6)',
    BG_DIVIDER: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',

    // Border colors - subtle like AI Hub (border-gray-200/50)
    BORDER_DEFAULT: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    BORDER_INPUT: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)',
    BORDER_LIGHT: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)',

    // Status colors
    SUCCESS_BG: 'var(--joy-palette-success-200, #AEFF82)',
    SUCCESS_TEXT: 'var(--joy-palette-success-800, #153B00)',
    WARNING_BG: 'var(--joy-palette-warning-300, #C4AC11)',

    // Map placeholder
    MAP_BG: isDark ? 'var(--joy-palette-neutral-800, #1F2937)' : 'var(--joy-palette-success-50, #E8F5E9)',
})

/** Legacy COLORS export for backward compatibility */
export const COLORS = getColors(false)

// ============================================================================
// TYPOGRAPHY
// ============================================================================

/** Typography constants */
export const TYPOGRAPHY = {
    FONT_FAMILY: 'Inter',

    // Font sizes
    SIZE_28: '20px',
    SIZE_20: '16px',
    SIZE_14: '13px',
    SIZE_13: '12px',
    SIZE_12: '11px',
    SIZE_11: '10px',
    SIZE_10: '9px',

    // Font weights
    WEIGHT_600: 600,
    WEIGHT_500: 500,
    WEIGHT_400: 400,
    WEIGHT_300: 300,

    // Line heights
    LINE_HEIGHT_100: '100%',
    LINE_HEIGHT_13: '13px',
    LINE_HEIGHT_14: '14px',
    LINE_HEIGHT_15: '15px',
    LINE_HEIGHT_16: '16px',
    LINE_HEIGHT_20: '20px',
} as const

// ============================================================================
// LAYOUT
// ============================================================================

/** Layout constants */
export const LAYOUT = {
    // Widths
    LEFT_COLUMN_WIDTH: '320px',
    MIDDLE_COLUMN_WIDTH: '320px',
    MAP_WIDTH: '410px',
    CALCULATOR_WIDTH: '160px',
    PROFITABILITY_CALCULATOR_WIDTH: '180px',
    COSTS_BREAKDOWN_WIDTH: '150px',
    CALCULATORS_WRAPPER_WIDTH: '340px',
    CALCULATOR_LABEL_WIDTH: '64px',
    CALCULATOR_INPUT_WIDTH: '96px',

    // Heights
    SECTION_HEIGHT: '264px',
    CALCULATOR_HEIGHT: '217px',
    ROW_HEIGHT_28: '28px',
    ROW_HEIGHT_56: '56px',

    // Gaps
    GAP_1: '1px',
    GAP_2: '2px',
    GAP_3: '3px',
    GAP_4: '3px',
    GAP_6: '4px',
    GAP_8: '6px',
    GAP_12: '8px',
    GAP_16: '12px',

    // Border radius
    RADIUS_6: '6px',
    RADIUS_8: '8px',
    RADIUS_12: '8px',

    // Padding
    PADDING_4: '3px',
    PADDING_8: '6px',
    PADDING_12: '8px',
    PADDING_16: '12px',
    INPUT_PADDING: '3px',
} as const

/** Icon sizes */
export const ICON_SIZE = {
    SMALL: '16px',
    MEDIUM: '20px',
} as const

// ============================================================================
// COMMON STYLES
// ============================================================================

/** Common reusable styles */
export const COMMON_STYLES = {
    // Large header text (for origin/destination in expanded row)
    HEADER_TEXT_LARGE: {
        fontSize: TYPOGRAPHY.SIZE_20,
        fontWeight: TYPOGRAPHY.WEIGHT_500,
        fontFamily: TYPOGRAPHY.FONT_FAMILY,
        lineHeight: TYPOGRAPHY.LINE_HEIGHT_20,
        letterSpacing: '-0.4px',
        color: 'var(--joy-palette-text-primary)',
    },

    // Vertical divider
    DIVIDER_VERTICAL: (height: string) => ({
        width: '1px',
        height,
        bgcolor: 'var(--joy-palette-divider)',
    }),

    // Full centered container
    CENTERED_CONTAINER: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },

    // Tab text style (origin/destination in tabs)
    TAB_TEXT: (isActive: boolean) => ({
        height: '16px',
        fontFamily: 'Inter',
        fontWeight: isActive ? 500 : 400,
        fontSize: '12px',
        lineHeight: '15.96px',
        letterSpacing: '0%',
        verticalAlign: 'middle',
        display: 'flex',
        alignItems: 'center',
        color: 'var(--joy-palette-text-primary)',
    }),

    TABLE_BODY_TEXT: {
        fontFamily: "'Inter', sans-serif",
        fontWeight: 400,
        fontSize: '12px',
        lineHeight: '100%',
        letterSpacing: '0%',
        verticalAlign: 'middle',
        color: 'var(--joy-palette-text-primary)',
        display: 'block',
    },

    // Tab text wrapper
    TAB_TEXT_WRAPPER: {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'flex-start' as const,
        padding: '0px 0px 0.95px',
        height: '16px',
    },
} as const

// ============================================================================
// STORAGE KEYS
// ============================================================================

/** LocalStorage key for current load (extension communication) */
export const CURRENT_LOAD_KEY = 'currentLoad'

/** LocalStorage key for extension detection */
export const HAS_EXTENSION_KEY = 'hasExtension'
